export type AudioDeviceOption = {
  deviceId: string;
  label: string;
};

export type AudioDeviceInventory = {
  inputs: AudioDeviceOption[];
  outputs: AudioDeviceOption[];
};

type AudioDeviceEnumerator = Pick<MediaDevices, "enumerateDevices">;
type MicrophoneMediaDevices = Pick<MediaDevices, "getUserMedia">;
type AudioOutputMediaDevices = {
  selectAudioOutput?: (options: { deviceId: string }) => Promise<{ deviceId: string }>;
};
type SinkAudioElement = {
  setSinkId?: (deviceId: string) => Promise<void>;
};

export type OpenMicrophoneResult = {
  stream: MediaStream;
  deviceId: string;
};

export type OutputRouteResult =
  | { mode: "selected"; deviceId: string }
  | { mode: "system"; deviceId: "default" };

export async function listAudioDevices(
  mediaDevices: AudioDeviceEnumerator,
): Promise<AudioDeviceInventory> {
  const devices = await mediaDevices.enumerateDevices();
  let unnamedInputCount = 0;
  let unnamedOutputCount = 0;
  const inputs: AudioDeviceOption[] = [];
  const outputs: AudioDeviceOption[] = [];
  const inputIds = new Set<string>();
  const outputIds = new Set<string>();

  for (const device of devices) {
    if (device.kind === "audioinput") {
      const deviceId = normalizeDeviceId(device.deviceId);
      if (inputIds.has(deviceId)) continue;
      inputIds.add(deviceId);
      unnamedInputCount += device.label ? 0 : 1;
      inputs.push({
        deviceId,
        label: device.label || `Microphone ${unnamedInputCount}`,
      });
    }

    if (device.kind === "audiooutput") {
      const deviceId = normalizeDeviceId(device.deviceId);
      if (outputIds.has(deviceId)) continue;
      outputIds.add(deviceId);
      unnamedOutputCount += device.label ? 0 : 1;
      outputs.push({
        deviceId,
        label: device.label || `Audio output ${unnamedOutputCount}`,
      });
    }
  }

  return { inputs, outputs };
}

export async function openMicrophone(
  mediaDevices: MicrophoneMediaDevices,
  deviceId: string,
): Promise<OpenMicrophoneResult> {
  const normalizedDeviceId = normalizeDeviceId(deviceId);
  try {
    const stream = await mediaDevices.getUserMedia(microphoneConstraints(normalizedDeviceId));
    return { stream, deviceId: normalizedDeviceId };
  } catch (caught) {
    if (
      normalizedDeviceId === "default" ||
      !isUnavailableSelectedDeviceError(caught)
    ) throw caught;
    const stream = await mediaDevices.getUserMedia(microphoneConstraints("default"));
    return { stream, deviceId: "default" };
  }
}

function microphoneConstraints(deviceId: string): MediaStreamConstraints {
  return {
    audio: {
      deviceId: deviceId === "default" ? undefined : { exact: deviceId },
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  };
}

function normalizeDeviceId(deviceId: string): string {
  return deviceId.trim() ? deviceId : "default";
}

function isUnavailableSelectedDeviceError(error: unknown): boolean {
  const name = error && typeof error === "object" && "name" in error
    ? String((error as { name: unknown }).name)
    : "";
  return name === "NotFoundError" ||
    name === "OverconstrainedError" ||
    name === "ConstraintNotSatisfiedError";
}

export async function replaceMicrophoneTrack(
  sender: Pick<RTCRtpSender, "replaceTrack">,
  oldStream: { getTracks(): Array<Pick<MediaStreamTrack, "stop">> },
  newStream: Pick<MediaStream, "getAudioTracks">,
): Promise<void> {
  const [newTrack] = newStream.getAudioTracks();
  if (!newTrack) {
    throw new Error("Selected microphone has no audio track");
  }

  await sender.replaceTrack(newTrack);
  oldStream.getTracks().forEach((track) => track.stop());
}

export function supportsOutputSelection(
  mediaDevices: AudioOutputMediaDevices,
  audioElement: SinkAudioElement,
): boolean {
  return (
    typeof mediaDevices.selectAudioOutput === "function" &&
    typeof audioElement.setSinkId === "function"
  );
}

export async function routeAudioOutput(
  mediaDevices: AudioOutputMediaDevices,
  audioElement: SinkAudioElement,
  deviceId: string,
  signal?: AbortSignal,
  onSinkWriteStart?: () => void,
): Promise<OutputRouteResult> {
  deviceId = normalizeDeviceId(deviceId);
  throwIfAborted(signal);
  if (typeof audioElement.setSinkId !== "function") {
    return { mode: "system", deviceId: "default" };
  }

  if (deviceId === "default") {
    throwIfAborted(signal);
    onSinkWriteStart?.();
    await audioElement.setSinkId("");
    throwIfAborted(signal);
    return { mode: "system", deviceId: "default" };
  }

  if (typeof mediaDevices.selectAudioOutput === "function") {
    const selectedDevice = await mediaDevices.selectAudioOutput({ deviceId });
    throwIfAborted(signal);
    onSinkWriteStart?.();
    await audioElement.setSinkId(selectedDevice.deviceId);
    throwIfAborted(signal);
    return { mode: "selected", deviceId: selectedDevice.deviceId };
  }

  throwIfAborted(signal);
  onSinkWriteStart?.();
  await audioElement.setSinkId(deviceId);
  throwIfAborted(signal);
  return { mode: "selected", deviceId };
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException("Audio output selection was cancelled", "AbortError");
}
